import { useEffect, useMemo, useRef, useState } from 'react';

import { ASSIGNABLE_ROLES, getRoleColor } from './roles';
import { useToast } from '../../contexts/ToastContext';
import { toUserErrorMessage } from '../../lib/apiError';
import { useUpdateUserRoles } from '../../services/api/hooks/useUpdateUserRoles';
import { useUserRolesPermissions } from '../../services/api/hooks/useUserRolesPermissions';
import type { UserV2Output } from '../../types/extranet-api.types';
import { IconX } from '../incidents/icons';

interface UserRolesModalProps {
  user: UserV2Output | null;
  onClose: () => void;
}

/**
 * Modale d'édition des rôles d'un utilisateur (POST /v1/users/:id/roles).
 *
 * **Stratégie zéro écrasement** (cf. warning Yohan) : on récupère les rôles
 * frais via `useUserRolesPermissions`, on les sépare en deux groupes :
 * - **assignables** : présents dans `ASSIGNABLE_ROLES` → édition via checkboxes
 * - **conservés** : rôles inconnus du UI → toujours réinjectés dans le payload
 *
 * À la submit, on POST `[...cochés, ...conservés]`. Aucun rôle ne disparaît
 * involontairement.
 */
export const UserRolesModal = ({ user, onClose }: UserRolesModalProps) => {
  const { data: permissions, isLoading } = useUserRolesPermissions(user?.id);
  const updateRoles = useUpdateUserRoles();
  const toast = useToast();
  const panelRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Quand les rôles frais arrivent, pré-cocher les assignables qui sont déjà
  // chez l'utilisateur. Les inconnus sont gérés à part (cf. preservedRoles).
  useEffect(() => {
    if (!permissions) return;
    const next = new Set<string>();
    for (const role of permissions.roles) {
      if ((ASSIGNABLE_ROLES as readonly string[]).includes(role)) {
        next.add(role);
      }
    }
    setSelected(next);
  }, [permissions]);

  useEffect(() => {
    if (!user) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [user, onClose]);

  const preservedRoles = useMemo(() => {
    if (!permissions) return [] as string[];
    return permissions.roles.filter(r => !(ASSIGNABLE_ROLES as readonly string[]).includes(r));
  }, [permissions]);

  if (!user) return null;

  const toggle = (role: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  };

  const handleSubmit = () => {
    const payload = [...Array.from(selected), ...preservedRoles];
    updateRoles.mutate(
      { userId: user.id, roles: payload },
      {
        onSuccess: () => {
          toast.success('Rôles mis à jour', `Les rôles de ${user.firstName} ${user.lastName} ont été enregistrés.`);
          onClose();
        },
        onError: err => {
          const { title, description } = toUserErrorMessage(err, 'Échec de la mise à jour');
          toast.error(title, description);
        },
      },
    );
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        onClick={onClose}
        aria-hidden
        className="absolute inset-0 bg-black/30 drawer-backdrop-in"
        style={{ backdropFilter: 'blur(2px)' }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-roles-modal-title"
        className="relative w-full max-w-md bg-white border border-zinc-200 rounded-md shadow-lg drawer-panel-in flex flex-col"
      >
        <header className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4">
          <div className="flex flex-col gap-0.5 min-w-0">
            <h2 id="user-roles-modal-title" className="text-sm font-medium text-zinc-900 truncate">
              Rôles de {user.firstName} {user.lastName}
            </h2>
            <p className="text-xs text-zinc-500 truncate font-mono">{user.email}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
          >
            <IconX width={16} height={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          {isLoading ? (
            <p className="text-xs text-zinc-500">Chargement des rôles…</p>
          ) : (
            <>
              <fieldset className="flex flex-col gap-2">
                <legend className="text-xs font-medium text-zinc-700 mb-1">Rôles assignables</legend>
                {ASSIGNABLE_ROLES.map(role => {
                  const tokens = getRoleColor(role);
                  const checked = selected.has(role);
                  return (
                    <label key={role} htmlFor={`role-${role}`} className="flex items-center gap-2.5 cursor-pointer">
                      <input
                        id={`role-${role}`}
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(role)}
                        className="h-3.5 w-3.5 accent-zinc-900"
                      />
                      <span
                        className={`inline-flex items-center rounded-md text-[10px] font-medium px-1.5 py-0.5 font-mono tabular-nums ${tokens.bg} ${tokens.text}`}
                      >
                        {role}
                      </span>
                    </label>
                  );
                })}
              </fieldset>

              {preservedRoles.length > 0 ? (
                <div className="flex flex-col gap-1.5 border-t border-zinc-100 pt-3">
                  <span className="text-xs font-medium text-zinc-700">Autres rôles conservés</span>
                  <p className="text-[11px] text-zinc-500">
                    Rôles attribués à cet utilisateur en dehors du périmètre éditable. Ils seront préservés à
                    l&apos;enregistrement.
                  </p>
                  <span className="inline-flex items-center gap-1 flex-wrap mt-1">
                    {preservedRoles.map(role => {
                      const tokens = getRoleColor(role);
                      return (
                        <span
                          key={role}
                          className={`inline-flex items-center rounded-md text-[10px] font-medium px-1.5 py-0.5 font-mono tabular-nums ${tokens.bg} ${tokens.text}`}
                        >
                          {role}
                        </span>
                      );
                    })}
                  </span>
                </div>
              ) : null}

              {updateRoles.error ? (
                <p className="text-xs px-3 py-2 bg-red-50 border border-red-100 text-red-700 rounded-md mt-2">
                  {(updateRoles.error as Error).message || "Échec de l'opération. Réessayer."}
                </p>
              ) : null}
            </>
          )}
        </div>

        <footer className="px-6 py-3 border-t border-zinc-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || updateRoles.isPending}
            className="h-9 px-4 text-sm font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {updateRoles.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </footer>
      </div>
    </div>
  );
};
